import { getCokkie } from "../../views/login/Login";
import { ImageData } from "../../store/labels/types";
import { FileUtil } from "../../utils/FileUtil";
import { ImageLoadManager, ImageLoadPriority } from "./ImageLoadManager";
import { ImageRepository } from "./ImageRepository";

type ImageManifestItem = {
  image_name?: string;
  image_url?: string;
};

export type LoadedAnnotationImage = {
  file: File;
  image: HTMLImageElement;
};

/** Loads and caches annotation images while retaining blob-backed canvas data. */
export class AnnotationImageLoader {
  private static taskId = "";
  private static sourceUrls = new Map<string, string>();
  private static refreshPromise: Promise<void> | null = null;
  private static generation = 0;

  public static configure(taskId: string, images: ImageData[]): void {
    AnnotationImageLoader.reset();
    AnnotationImageLoader.taskId = taskId;
    images.forEach((image) => {
      if (image.sourceUrl) {
        AnnotationImageLoader.sourceUrls.set(image.image_id, image.sourceUrl);
      }
    });
  }

  public static async load(
    imageData: ImageData,
    priority: ImageLoadPriority = "background"
  ): Promise<LoadedAnnotationImage> {
    const cached = ImageRepository.getById(imageData.image_id);
    if (cached) {
      return { file: imageData.fileData, image: cached };
    }

    return ImageLoadManager.load(
      imageData.image_id,
      async (signal) => AnnotationImageLoader.loadUncached(
        imageData,
        signal,
        AnnotationImageLoader.generation
      ),
      priority
    );
  }

  public static prefetch(images: ImageData[], activeIndex: number): void {
    const candidates = [activeIndex + 1, activeIndex - 1, activeIndex + 2];
    candidates.forEach((index) => {
      const imageData = images[index];
      if (!imageData) return;
      AnnotationImageLoader.load(imageData, "background").catch(() => undefined);
    });
  }

  public static async hydrate(
    images: ImageData[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<void> {
    let completed = 0;
    const pending = images.filter(
      (image) => !ImageRepository.getById(image.image_id)
    );
    const total = pending.length;
    onProgress?.(completed, total);
    await Promise.all(
      pending.map((image) =>
        AnnotationImageLoader.load(image, "background").then(() => {
          completed += 1;
          onProgress?.(completed, total);
        })
      )
    );
  }

  public static reset(): void {
    AnnotationImageLoader.generation += 1;
    ImageLoadManager.reset();
    ImageRepository.clear();
    AnnotationImageLoader.taskId = "";
    AnnotationImageLoader.sourceUrls.clear();
    AnnotationImageLoader.refreshPromise = null;
  }

  private static async loadUncached(
    imageData: ImageData,
    signal: AbortSignal,
    generation: number
  ): Promise<LoadedAnnotationImage> {
    let file = imageData.fileData;
    const sourceUrl = AnnotationImageLoader.sourceUrls.get(imageData.image_id);
    if (sourceUrl) {
      try {
        file = await FileUtil.downloadFile(sourceUrl, imageData.image_id, signal);
      } catch (error) {
        if ((error as { status?: number }).status !== 403) throw error;
        await AnnotationImageLoader.refreshSources();
        const refreshedUrl = AnnotationImageLoader.sourceUrls.get(imageData.image_id);
        if (!refreshedUrl) throw error;
        file = await FileUtil.downloadFile(refreshedUrl, imageData.image_id, signal);
      }
    }

    const image = await FileUtil.loadImage(file);
    if (generation !== AnnotationImageLoader.generation) {
      if (
        image.src.startsWith("blob:") &&
        typeof URL.revokeObjectURL === "function"
      ) {
        URL.revokeObjectURL(image.src);
      }
      throw new DOMException("Stale annotation image request", "AbortError");
    }
    imageData.fileData = file;
    imageData.loadStatus = true;
    ImageRepository.storeImage(imageData.image_id, image);
    return { file, image };
  }

  private static async refreshSources(): Promise<void> {
    if (AnnotationImageLoader.refreshPromise) {
      return AnnotationImageLoader.refreshPromise;
    }
    AnnotationImageLoader.refreshPromise = (async () => {
      const token = getCokkie("jwtToken");
      const response = await fetch(
        `${window["ENV"].API_URL}/tasks/images/${AnnotationImageLoader.taskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Unable to refresh image URLs (${response.status})`);
      }
      const payload = await response.json();
      const items = Object.values(payload?.image || {}) as ImageManifestItem[];
      items.forEach((item) => {
        if (item.image_name && item.image_url) {
          AnnotationImageLoader.sourceUrls.set(item.image_name, item.image_url);
        }
      });
    })().finally(() => {
      AnnotationImageLoader.refreshPromise = null;
    });
    return AnnotationImageLoader.refreshPromise;
  }
}
